import { useState } from 'react';
import { AlertTriangle, Phone, X, Send, Wifi, WifiOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { queueOfflineAction } from '../utils/offlineSync';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SOSButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  const emergencyContacts = [
    { name: 'Emergency Services', number: '911' },
    { name: 'Agency Hotline', number: '1-800-CARE' },
    { name: 'On-Call Supervisor', number: 'Contact via app' }
  ];

  const sendAlert = async () => {
    setSending(true);
    const alertData = {
      alert_type: 'sos',
      message: message || 'Emergency SOS Alert!',
      location: null
    };

    // Try to get GPS location
    if ('geolocation' in navigator) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        alertData.location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        };
      } catch {
        // GPS unavailable, continue without location
      }
    }

    if (navigator.onLine) {
      try {
        await axios.post(`${API_URL}/api/emergency/alert`, alertData, { withCredentials: true });
        toast.success('Emergency alert sent to your organization!');
        setIsOpen(false);
        setMessage('');
      } catch (err) {
        toast.error('Failed to send alert - queuing for later');
        await queueOfflineAction({ type: 'emergency_alert', data: alertData });
      }
    } else {
      // Offline - queue for sync
      await queueOfflineAction({ type: 'emergency_alert', data: alertData });
      toast.warning('Alert queued - will be sent when you\'re back online');
      setIsOpen(false);
      setMessage('');
    }
    setSending(false);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        data-testid="sos-button"
        className="sos-button fixed bottom-24 lg:bottom-6 left-6 z-40 w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 
                   flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.5)] 
                   transition-all duration-200 hover:scale-105"
        aria-label="SOS Emergency Button"
      >
        <AlertTriangle className="w-8 h-8 text-white" strokeWidth={2} />
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="glass-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-foreground flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              Emergency Assistance
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-muted-foreground text-sm">
              If you are in immediate danger, please call emergency services directly.
            </p>

            {/* Online/Offline indicator */}
            <div className="flex items-center gap-2 text-xs">
              {navigator.onLine ? (
                <>
                  <Wifi className="w-3 h-3 text-[#A8E6CF]" />
                  <span className="text-[#A8E6CF]">Online - alert will be sent immediately</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 text-amber-400" />
                  <span className="text-amber-400">Offline - alert will be queued and sent when connected</span>
                </>
              )}
            </div>

            {/* SOS Alert Button */}
            <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20 space-y-3">
              <Input value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Optional: Describe the emergency..."
                className="bg-muted border-border text-foreground"
                data-testid="sos-message-input" />
              <Button className="w-full bg-red-600 hover:bg-red-500 text-white"
                onClick={sendAlert} disabled={sending} data-testid="send-sos-btn">
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending Alert...' : 'Send SOS Alert'}
              </Button>
            </div>

            <div className="space-y-3">
              {emergencyContacts.map((contact, index) => (
                <div key={index}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted border border-border">
                  <div>
                    <p className="text-foreground font-medium">{contact.name}</p>
                    <p className="text-muted-foreground text-sm">{contact.number}</p>
                  </div>
                  <Button variant="outline" size="sm"
                    className="border-border hover:bg-muted/50"
                    data-testid={`call-${contact.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <Phone className="w-4 h-4 mr-2" />Call
                  </Button>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-border">
              <Button onClick={() => setIsOpen(false)}
                className="w-full bg-muted hover:bg-[#384752] text-foreground"
                data-testid="close-sos-modal">
                <X className="w-4 h-4 mr-2" />Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SOSButton;
